import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Storefront, UploadSimple } from "@phosphor-icons/react";

export default function StoreSetup() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/giris");
    if (user.role !== "corporate") {
      toast.error("Sadece kurumsal hesaplar mağaza açabilir");
      nav("/panelim");
      return;
    }
    if (user.store) {
      setName(user.store.name || "");
      setBio(user.store.bio || "");
      setLogo(user.store.logo);
      setBanner(user.store.banner);
    }
  }, [user, loading, nav]);

  const upload = async (e, setter) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.path);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error("Yükleme başarısız");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/store/setup", { store_name: name, bio, logo, banner });
      await refresh();
      toast.success("Mağaza güncellendi");
      nav(`/magaza/${data.store.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="store-setup-page">
      <div className="border-b border-stone-200 pb-6 mb-8 flex items-center gap-3">
        <Storefront size={32} weight="bold" />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-stone-500">Kurumsal</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Mağaza Ayarları</h1>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Mağaza Adı</label>
          <input
            data-testid="store-name"
            required minLength={3} maxLength={60} value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-stone-50 border border-stone-200 px-4 py-3 focus:outline-none focus:border-stone-950 focus:bg-white"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Kısa Açıklama</label>
          <textarea
            data-testid="store-bio"
            rows={3} maxLength={500} value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="w-full bg-stone-50 border border-stone-200 px-4 py-3 focus:outline-none focus:border-stone-950 focus:bg-white"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Logo</label>
            <div className="border border-stone-200 bg-white p-4">
              {logo && <div className="w-24 h-24 border border-stone-200 mb-3 overflow-hidden">
                <img src={logo.startsWith("http") ? logo : fileUrl(logo)} alt="" className="w-full h-full object-cover" />
              </div>}
              <label className="flex items-center justify-center gap-2 border border-dashed border-stone-300 bg-stone-50 py-4 cursor-pointer hover:border-stone-900">
                <UploadSimple size={16} /> <span className="text-xs font-bold">Logo yükle</span>
                <input type="file" accept="image/*" onChange={(e) => upload(e, setLogo)} className="hidden" data-testid="store-logo-input" />
              </label>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Banner</label>
            <div className="border border-stone-200 bg-white p-4">
              {banner && <div className="w-full h-24 border border-stone-200 mb-3 overflow-hidden">
                <img src={banner.startsWith("http") ? banner : fileUrl(banner)} alt="" className="w-full h-full object-cover" />
              </div>}
              <label className="flex items-center justify-center gap-2 border border-dashed border-stone-300 bg-stone-50 py-4 cursor-pointer hover:border-stone-900">
                <UploadSimple size={16} /> <span className="text-xs font-bold">Banner yükle</span>
                <input type="file" accept="image/*" onChange={(e) => upload(e, setBanner)} className="hidden" data-testid="store-banner-input" />
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
          <button
            data-testid="store-save-btn"
            disabled={busy}
            className="bg-stone-950 text-white px-6 py-3 font-bold hover:bg-stone-800 transition-colors disabled:opacity-60"
          >
            {busy ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </form>
    </div>
  );
}
